// * Importing constants and API configurations
import { API_URI } from "../../constants/consts";
import { api } from "../../services/apis/configs/axiosConfigs";

// ? Importing necessary types
import type {
  AddPermissionsRequest,
  AddPermissionsResponse,
  GetUserPermissionsResponse,
} from "../permissions/types/types";

/**
 * permissionAPI is a utility object for managing permission-related operations.
 */
export const permissionAPI = {
  /**
   * Adds or updates permissions for a specific user.
   *
   * @param {string} userId - Target user's ID.
   * @param {AddPermissionsRequest} permissionsData - Data of the permissions to be added/updated.
   * @param {string} token - Authentication token sent in the request headers.
   *
   * @returns {Promise<AddPermissionsResponse>} - Returns the API response after adding/updating permissions.
   */
  addPermissions: async function (
    userId: string,
    permissionsData: AddPermissionsRequest,
    token: string
  ): Promise<AddPermissionsResponse> {
    // * Define the API URL to add permissions
    const url = `${API_URI}/admin/private/users/${userId}/permissions`;

    try {
      const response = await api.request({
        url: url,
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        data: permissionsData,
      });

      // * Return the data according to the AddPermissionsResponse structure
      return response.data as AddPermissionsResponse;
    } catch (error: any) {
      console.error("Error adding permissions:", error);
      // ! Check if the error has a message before throwing it
      const errorMessage =
        error.response?.data?.message || "Failed to add permissions.";
      throw new Error(errorMessage);
    }
  },

  getUserPermissions: async function (
    userId: string,
    token: string
  ): Promise<GetUserPermissionsResponse> {
    const url = `${API_URI}/admin/private/users/${userId}/permissions`;

    try {
      const response = await api.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });


      return response.data as GetUserPermissionsResponse;
    } catch (error: any) {
      console.error("Error getting user permissions:", error);
      const errorMessage =
        error.response?.data?.message ||
        "Failed to get user permissions.";
      throw new Error(errorMessage);
    }
  },
};
